import {
  type CustomerApplicationFieldValueContract,
  type CustomerFieldMappingTarget,
  customerFieldTargetOptions,
} from '@/contracts/customer-application-binding'
import type { CustomerFieldMappingContract } from '@/contracts/customers'
import type { QuestionnaireAnswer } from '@/lib/business-workflow'
import { DataClientRequestError, requestApiEnvelope } from '@/lib/data-client/http'
import { isApiDataClientMode } from '@/lib/data-client/mode'
import {
  clearInvalidConditionalAnswers,
  clearInvalidDependentAnswers,
} from '@/lib/dependent-choices'
import { isWelcomeScreenEnabled } from '@/lib/form-welcome-screen'
import { getNextQuestionId } from '@/lib/logic-evaluator'
import { getQuestionValidationError } from '@/lib/question-validation'
import {
  formatTaiwanAddressAnswer,
  reconcileTaiwanAddressSourceAnswers,
} from '@/lib/taiwan-address'
import { useAuthStore } from '@/stores/authStore'
import { type NewApplicationInput, useBusinessWorkflowStore } from '@/stores/businessWorkflowStore'
import type { SessionUser } from '@/types/api'
import { isLayoutQuestionType } from '@/types/form'
import type { AnswerValue, PublicForm, Question, QuestionGroupReference } from '@/types/form'
import { create } from 'zustand'

export type FormPlayerMode = 'live' | 'preview'
export type FormPlayerPhase = 'welcome' | 'questions' | 'thankyou'

type LoadFormOptions = {
  mode?: FormPlayerMode
  initialPhase?: Extract<FormPlayerPhase, 'welcome' | 'questions'>
}

interface FormPlayerState {
  form: PublicForm | null
  questions: Question[]
  mode: FormPlayerMode
  currentIndex: number
  answers: Map<string, AnswerValue>
  direction: 'forward' | 'backward'
  phase: FormPlayerPhase
  isSubmitting: boolean
  sessionId: string
  sessionStartTime: number
  questionStartTime: number
  validationError: string | null
  validationErrorQuestionId: string | null
  submissionMetadataPatch: Record<string, unknown> | null
  load: (form: PublicForm, options?: LoadFormOptions) => void
  replaceRuntimeForm: (form: PublicForm) => void
  refreshAutofillAnswers: () => void
  clearAnswersByQuestionIds: (questionIds: string[]) => void
  setSubmissionMetadataPatch: (patch: Record<string, unknown> | null) => void
  goNext: () => Promise<void>
  goBack: () => void
  setAnswer: (questionId: string, value: AnswerValue) => void
  submit: () => Promise<void>
}

type PublicFormSubmitContract = {
  responseId?: string
  sessionId: string
  status?: string
  answerCount?: number
}

const customerFieldLabelMap = new Map(
  customerFieldTargetOptions.map((option) => [option.value, option.shortLabel] as const),
)

export function getFormProgressStorageKey(formId: string) {
  let userKey = 'anonymous'
  try {
    const raw = localStorage.getItem('miaoform_demo_user')
    if (raw) {
      const parsed = JSON.parse(raw) as { email?: string; userId?: string }
      userKey = parsed.email ?? parsed.userId ?? userKey
    }
  } catch {
    userKey = 'anonymous'
  }
  return `miaoform_${formId}_${userKey}_progress`
}

function getDemoSessionUser(): SessionUser | null {
  try {
    const raw = localStorage.getItem('miaoform_demo_user')
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<SessionUser>
    if (!parsed.email) return null
    return {
      userId: parsed.userId ?? parsed.email,
      email: parsed.email,
      name: parsed.name ?? parsed.email,
      avatarUrl: parsed.avatarUrl,
      workspaceId: parsed.workspaceId ?? 'mock-workspace',
      workspaceName: parsed.workspaceName ?? 'MiaoForm Demo',
      plan: parsed.plan ?? 'teams',
      role: parsed.role ?? 'member',
    }
  } catch {
    return null
  }
}

function isCustomerOnboardingForm(form: PublicForm) {
  return (
    form.settings.customerWorkflow === 'customer_onboarding' ||
    form.settings.isCustomerIntake === true
  )
}

function resolveInitialPhase(form: PublicForm, requestedPhase?: LoadFormOptions['initialPhase']) {
  if (requestedPhase === 'welcome') {
    return isWelcomeScreenEnabled(form) ? 'welcome' : 'questions'
  }
  if (requestedPhase) return requestedPhase
  return isWelcomeScreenEnabled(form) ? 'welcome' : 'questions'
}

function labelForTarget(target: CustomerFieldMappingTarget) {
  return customerFieldLabelMap.get(target) ?? target
}

function normalizeAnswerValue(value: AnswerValue | undefined) {
  if (value === undefined || value === null) return ''
  if (Array.isArray(value)) return value.map(String).filter(Boolean).join('、')
  if (typeof value === 'object') {
    const address = formatTaiwanAddressAnswer(value)
    if (address) return address
    return Object.entries(value)
      .filter(([, item]) => item !== undefined && item !== null && item !== '')
      .map(([key, item]) => `${key}: ${String(item)}`)
      .join('；')
  }
  return String(value).trim()
}

function toNumber(value: string | undefined) {
  const parsed = Number(String(value ?? '').replace(/[^\d.-]/g, ''))
  return Number.isFinite(parsed) ? parsed : 0
}

function inferTargetFromQuestion(question: Question): CustomerFieldMappingTarget | null {
  const configuredTarget = question.settings.customerFieldTarget
  if (
    typeof configuredTarget === 'string' &&
    customerFieldLabelMap.has(configuredTarget as CustomerFieldMappingTarget)
  ) {
    return configuredTarget as CustomerFieldMappingTarget
  }

  const text = `${question.title} ${question.description ?? ''}`.toLocaleLowerCase()
  if (text.includes('統編') || text.includes('tax')) return 'taxId'
  if (text.includes('產業')) return 'industry'
  if (text.includes('公司規模') || text.includes('人數')) return 'companySize'
  if (text.includes('公司地址') || text.includes('地址')) return 'companyAddress'
  if (text.includes('公司電話')) return 'companyPhone'
  if (text.includes('網站') || text.includes('網址')) return 'website'
  if (text.includes('職稱')) return 'contactTitle'
  if (text.includes('email') || text.includes('電子郵件')) return 'contactEmail'
  if (text.includes('電話') || text.includes('手機')) return 'contactPhone'
  if (text.includes('決策')) return 'decisionRole'
  if (text.includes('付款')) return 'paymentTerms'
  if (text.includes('授信')) return 'creditLimit'
  if (text.includes('預估') || text.includes('金額') || text.includes('預算'))
    return 'expectedValue'
  if (text.includes('業務區域') || text.includes('區域')) return 'salesRegion'
  if (text.includes('需求') || text.includes('摘要')) return 'needSummary'
  if (text.includes('公司名稱') || text.includes('客戶名稱')) return 'companyName'
  if (text.includes('聯絡人') || text.includes('姓名')) return 'contactName'
  return null
}

export function buildCustomerApplicationFromFormSubmission({
  form,
  answers,
  fieldMappings,
  sessionId,
}: {
  form: PublicForm
  answers: Map<string, AnswerValue>
  fieldMappings: CustomerFieldMappingContract[]
  sessionId: string
}): {
  input: NewApplicationInput
  fieldValues: CustomerApplicationFieldValueContract[]
  applicationAnswers: QuestionnaireAnswer[]
} {
  const fieldPatch: Partial<Record<CustomerFieldMappingTarget, string>> = {}
  const fieldValues: CustomerApplicationFieldValueContract[] = []
  const mappings = fieldMappings.filter((mapping) => mapping.formId === form.id)
  const mappingByQuestionId = new Map(mappings.map((mapping) => [mapping.questionId, mapping]))

  const applicationAnswers = form.questions
    .filter((question) => !isLayoutQuestionType(question.type))
    .map((question) => {
      const answer = normalizeAnswerValue(answers.get(question.id))
      const mapping = mappingByQuestionId.get(question.id)
      const target = mapping?.targetField ?? inferTargetFromQuestion(question)
      if (target && answer && !fieldPatch[target]) {
        fieldPatch[target] = answer
        fieldValues.push({
          fieldKey: target,
          label: mapping?.targetLabel ?? labelForTarget(target),
          value: answer,
          originalValue: answer,
          sourceState: 'prefilled_from_question',
          sourceQuestionId: question.id,
          sourceQuestionTitle: question.title,
          sourceAnswerId: `${sessionId}:${question.id}`,
        })
      }

      return {
        id: `${sessionId}:${question.id}`,
        questionTitle: question.title,
        questionType: question.type,
        answer,
        mappedField: target ?? '',
      }
    })
    .filter((answer) => answer.answer)

  const input: NewApplicationInput = {
    formId: form.id,
    formTitle: form.title,
    formDescription:
      form.description || '填答送出後由 MiaoForm mock workflow 自動建立的新客戶申請。',
    companyName:
      firstFieldValue(fieldPatch, 'customerShortName', 'companyName', 'companyFullName') ||
      fieldPatch.contactName ||
      '未命名公司',
    taxId: fieldPatch.taxId || '',
    industry: firstFieldValue(fieldPatch, 'customerChannel', 'industry') || '',
    customerType: firstFieldValue(fieldPatch, 'customerType') || '',
    departmentName: firstFieldValue(fieldPatch, 'department') || '',
    companySize: firstFieldValue(fieldPatch, 'companySize', 'employeeCountRange') || '',
    website: fieldPatch.website || '',
    companyAddress:
      firstFieldValue(
        fieldPatch,
        'companyAddress',
        'deliveryAddress',
        'registeredAddress',
        'invoiceAddress',
      ) || '',
    companyPhone: firstFieldValue(fieldPatch, 'companyPhone', 'phone1') || '',
    contactName: fieldPatch.contactName || '',
    contactTitle: firstFieldValue(fieldPatch, 'contactTitle') || '',
    contactEmail: firstFieldValue(fieldPatch, 'contactEmail', 'email', 'billingEmail') || '',
    contactPhone: firstFieldValue(fieldPatch, 'contactPhone', 'phone1', 'phone2') || '',
    decisionRole: fieldPatch.decisionRole || '',
    paymentTerms: firstFieldValue(fieldPatch, 'paymentTerms') || '',
    creditLimit: toNumber(fieldPatch.creditLimit),
    salesRegion: firstFieldValue(fieldPatch, 'salesRegion', 'region') || '',
    expectedValue: toNumber(fieldPatch.expectedValue),
    source: '問卷填答送出',
    needSummary:
      firstFieldValue(fieldPatch, 'needSummary', 'remark') ||
      '由填答送出自動建立，請主管檢查欄位來源。',
    internalNotes:
      'frontend-only mock-safe：填答完成後自動建立建檔申請；未呼叫 Email、Webhook、DB 或外部 ERP。',
  }

  return { input, fieldValues, applicationAnswers }
}

function firstFieldValue(
  fieldPatch: Partial<Record<CustomerFieldMappingTarget, string>>,
  ...fields: CustomerFieldMappingTarget[]
) {
  return fields.map((field) => fieldPatch[field]).find((value) => value?.trim()) ?? ''
}

function isAnswerRecordValue(value: unknown): value is string | number | boolean | string[] | null {
  return (
    value === null ||
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean' ||
    (Array.isArray(value) && value.every((item) => typeof item === 'string'))
  )
}

function getDefaultAnswerValue(question: Question): AnswerValue | undefined {
  const value = question.settings.defaultValue
  if (value === undefined) return undefined
  if (isAnswerRecordValue(value)) return value
  if (isRecord(value)) {
    const entries = Object.entries(value).filter(
      (entry): entry is [string, string | number | boolean | string[] | null] =>
        isAnswerRecordValue(entry[1]),
    )
    return Object.fromEntries(entries) as AnswerValue
  }
  return undefined
}

function getAutofillAnswerValue(question: Question): AnswerValue | undefined {
  const target = question.settings.customerFieldTarget
  const dbFieldName = question.settings.dbFieldName
  const shouldAutofillSubmitter =
    target === 'submittedBy' ||
    dbFieldName === 'submitted_by' ||
    question.title.trim() === '填單人員'
  if (!shouldAutofillSubmitter) return undefined

  const user = useAuthStore.getState().user ?? getDemoSessionUser()
  const submitterName = normalizeDisplayName(user?.name) || normalizeEmail(user?.email)
  return submitterName || undefined
}

function isBlankAutofillAnswerValue(value: AnswerValue | undefined) {
  if (value === undefined || value === null) return true
  if (typeof value === 'string') return value.trim().length === 0
  return false
}

function normalizeFormAnswers(questions: Question[], answers: Map<string, AnswerValue>) {
  return clearInvalidConditionalAnswers(
    questions,
    reconcileTaiwanAddressSourceAnswers(
      questions,
      clearInvalidDependentAnswers(questions, answers),
    ),
  )
}

function applyDefaultAnswers(questions: Question[], answers = new Map<string, AnswerValue>()) {
  const nextAnswers = new Map(answers)
  for (const question of questions) {
    const autofillValue = getAutofillAnswerValue(question)
    if (autofillValue !== undefined && isBlankAutofillAnswerValue(nextAnswers.get(question.id))) {
      nextAnswers.set(question.id, autofillValue)
      continue
    }
    if (nextAnswers.has(question.id)) continue
    const defaultValue = autofillValue ?? getDefaultAnswerValue(question)
    if (defaultValue !== undefined) nextAnswers.set(question.id, defaultValue)
  }
  return normalizeFormAnswers(questions, nextAnswers)
}

function areAnswerMapsEqual(left: Map<string, AnswerValue>, right: Map<string, AnswerValue>) {
  if (left.size !== right.size) return false
  for (const [key, value] of left.entries()) {
    if (right.get(key) !== value) return false
  }
  return true
}

function persistProgressSnapshot(state: FormPlayerState, answers: Map<string, AnswerValue>) {
  const formId = state.form?.id
  if (!formId || state.mode !== 'live') return
  localStorage.setItem(
    getFormProgressStorageKey(formId),
    JSON.stringify({
      currentIndex: state.currentIndex,
      answers: Array.from(answers.entries()),
    }),
  )
}

function createCustomerApplicationForSubmission(state: FormPlayerState) {
  if (!state.form || !isCustomerOnboardingForm(state.form)) return
  const workflow = useBusinessWorkflowStore.getState()
  const { input, fieldValues, applicationAnswers } = buildCustomerApplicationFromFormSubmission({
    form: state.form,
    answers: state.answers,
    fieldMappings: workflow.fieldMappings,
    sessionId: state.sessionId,
  })
  workflow.submitApplication(input, getDemoSessionUser(), {
    fieldValues,
    answers: applicationAnswers,
  })
}

function serializePublicSubmitAnswers(answers: Map<string, AnswerValue>) {
  return Object.fromEntries(answers.entries())
}

function createPublicSubmitMetadata(state: FormPlayerState) {
  const user = useAuthStore.getState().user ?? getDemoSessionUser()
  const workflow = useBusinessWorkflowStore.getState()
  const submittedByEmail = normalizeEmail(user?.email) || 'authenticated-user@example.invalid'
  const submittedByName = normalizeDisplayName(user?.name) || submittedByEmail
  const reviewer = resolveReviewRecipientForSubmitter(user, submittedByEmail, submittedByName)

  return {
    source: 'public_form_player',
    formId: state.form?.id,
    submittedByEmail,
    submittedByName,
    supervisorEmail: reviewer.email,
    supervisorName: reviewer.name,
    reviewRecipientSource: reviewer.source,
    submitterProductionRole: user?.productionRole ?? null,
    submitterBusinessRole: user?.businessRole ?? null,
    teamId:
      workflow.salesUsers.find((salesUser) => normalizeEmail(salesUser.email) === submittedByEmail)
        ?.teamId ?? user?.teamId,
    teamName:
      workflow.salesUsers.find((salesUser) => normalizeEmail(salesUser.email) === submittedByEmail)
        ?.teamName ?? undefined,
    ...(state.submissionMetadataPatch ?? {}),
  }
}

function resolveReviewRecipientForSubmitter(
  user: SessionUser | null,
  submittedByEmail: string,
  submittedByName: string,
) {
  const workflow = useBusinessWorkflowStore.getState()
  const salesUser = workflow.salesUsers.find(
    (item) => normalizeEmail(item.email) === submittedByEmail,
  )
  const isAdminSubmitter =
    user?.productionRole === 'super_admin' ||
    user?.productionRole === 'admin' ||
    user?.businessRole === 'admin'

  if (isAdminSubmitter) {
    return {
      email: submittedByEmail,
      name: submittedByName,
      source: 'admin_self',
    }
  }

  const managerEmail =
    normalizeEmail(user?.managerEmail) || normalizeEmail(salesUser?.supervisorEmail)
  if (managerEmail) {
    const manager = workflow.salesUsers.find((item) => normalizeEmail(item.email) === managerEmail)
    return {
      email: managerEmail,
      name:
        normalizeDisplayName(manager?.name) ||
        normalizeDisplayName(salesUser?.supervisorName) ||
        '主管',
      source: 'sales_manager',
    }
  }

  return {
    email: submittedByEmail,
    name: submittedByName,
    source: 'submitter_self_fallback',
  }
}

async function submitPublicFormResponseToApi(state: FormPlayerState) {
  if (!state.form) throw new Error('問卷尚未載入，無法送出。')

  return requestApiEnvelope<PublicFormSubmitContract>(
    `/api/public/forms/${encodeURIComponent(state.form.id)}/responses`,
    {
      method: 'POST',
      body: JSON.stringify({
        sessionId: state.sessionId,
        answers: serializePublicSubmitAnswers(state.answers),
        metadata: createPublicSubmitMetadata(state),
      }),
    },
  )
}

function normalizeEmail(value: unknown) {
  if (typeof value !== 'string') return ''
  return value.trim().toLowerCase()
}

function normalizeDisplayName(value: unknown) {
  if (typeof value !== 'string') return ''
  return value.trim()
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === 'object' && !Array.isArray(value))
}

function getStringField(source: Record<string, unknown>, key: string) {
  const value = source[key]
  return typeof value === 'string' && value.trim() ? value.trim() : null
}

function resolvePublicSubmitError(
  error: unknown,
  questions: Question[],
): { message: string; questionId: string | null } {
  const rawMessage = error instanceof Error ? error.message : ''
  const fallbackMessage = '問卷送出失敗，請稍後再試。'

  if (error instanceof DataClientRequestError && error.code === 'missing_required_answer') {
    const details = isRecord(error.details) ? error.details : null
    const questionId = details ? getStringField(details, 'questionId') : null
    const question = questionId ? questions.find((item) => item.id === questionId) : null
    const questionTitle =
      question?.title ?? (details ? getStringField(details, 'questionTitle') : null)
    return {
      message: questionTitle ? `${questionTitle}：請完成此題` : fallbackMessage,
      questionId,
    }
  }

  if (
    error instanceof DataClientRequestError &&
    (error.code === 'invalid_choice_answer' || rawMessage.includes('Invalid option for question'))
  ) {
    const questionId = resolveQuestionIdFromTechnicalMessage(rawMessage, error.details)
    const question = questionId ? questions.find((item) => item.id === questionId) : null
    return {
      message: question
        ? `${question.title}：此題選項已更新，請重新選擇。`
        : '有題目選項已更新，請重新選擇後再送出。',
      questionId,
    }
  }

  if (
    error instanceof DataClientRequestError &&
    (error.code === 'invalid_numeric_answer' || rawMessage.includes('Invalid numeric answer'))
  ) {
    const questionId = resolveQuestionIdFromTechnicalMessage(rawMessage, error.details)
    const question = questionId ? questions.find((item) => item.id === questionId) : null
    return {
      message: question
        ? `${question.title}：數字格式不正確，請重新填寫。`
        : '有題目的數字格式不正確，請重新填寫後再送出。',
      questionId,
    }
  }

  const missingRequiredMatch = rawMessage.match(
    /Missing required answer for question ([A-Za-z0-9-]+)/,
  )
  const questionId = missingRequiredMatch?.[1] ?? null
  if (questionId) {
    const question = questions.find((item) => item.id === questionId)
    if (question) {
      return {
        message: `${question.title}：請完成此題`,
        questionId,
      }
    }
  }

  return { message: fallbackMessage, questionId: null }
}

function resolveQuestionIdFromTechnicalMessage(message: string, details: unknown) {
  const detailRecord = isRecord(details) ? details : null
  const detailQuestionId = detailRecord ? getStringField(detailRecord, 'questionId') : null
  if (detailQuestionId) return detailQuestionId

  return (
    message.match(/question ([A-Za-z0-9-]+)/)?.[1] ??
    message.match(/for question ([A-Za-z0-9-]+)/)?.[1] ??
    null
  )
}

export const useFormPlayerStore = create<FormPlayerState>((set, get) => ({
  form: null,
  questions: [],
  mode: 'live',
  currentIndex: 0,
  answers: new Map(),
  direction: 'forward',
  phase: 'welcome',
  isSubmitting: false,
  sessionId: crypto.randomUUID(),
  sessionStartTime: Date.now(),
  questionStartTime: Date.now(),
  validationError: null,
  validationErrorQuestionId: null,
  submissionMetadataPatch: null,
  load: (form, options = {}) => {
    const mode = options.mode ?? 'live'
    const phase = resolveInitialPhase(form, options.initialPhase)
    if (mode === 'preview') {
      const answers = applyDefaultAnswers(form.questions)
      set({
        form,
        questions: form.questions,
        mode,
        currentIndex: 0,
        answers,
        direction: 'forward',
        phase,
        isSubmitting: false,
        sessionId: crypto.randomUUID(),
        sessionStartTime: Date.now(),
        questionStartTime: Date.now(),
        validationError: null,
        validationErrorQuestionId: null,
        submissionMetadataPatch: null,
      })
      return
    }

    const stored = localStorage.getItem(getFormProgressStorageKey(form.id))
    if (stored) {
      try {
        const parsed = JSON.parse(stored) as {
          currentIndex: number
          answers: Array<[string, AnswerValue]>
        }
        set({
          form,
          questions: form.questions,
          mode,
          currentIndex: parsed.currentIndex,
          answers: applyDefaultAnswers(form.questions, new Map(parsed.answers)),
          phase: 'questions',
          validationError: null,
          validationErrorQuestionId: null,
          submissionMetadataPatch: null,
        })
        return
      } catch {
        localStorage.removeItem(getFormProgressStorageKey(form.id))
      }
    }
    set({
      form,
      questions: form.questions,
      mode,
      currentIndex: 0,
      answers: applyDefaultAnswers(form.questions),
      direction: 'forward',
      phase,
      isSubmitting: false,
      sessionId: crypto.randomUUID(),
      sessionStartTime: Date.now(),
      questionStartTime: Date.now(),
      validationError: null,
      validationErrorQuestionId: null,
      submissionMetadataPatch: null,
    })
  },
  replaceRuntimeForm: (form) => {
    const current = get()
    const questionIds = new Set(form.questions.map((question) => question.id))
    const answers = new Map(
      Array.from(current.answers.entries()).filter(([questionId]) => questionIds.has(questionId)),
    )
    const nextAnswers = applyDefaultAnswers(form.questions, answers)
    const currentQuestionId = current.questions[current.currentIndex]?.id
    const nextIndex = currentQuestionId
      ? Math.max(
          0,
          form.questions.findIndex((question) => question.id === currentQuestionId),
        )
      : current.currentIndex
    const nextState = {
      form,
      questions: form.questions,
      currentIndex: nextIndex < 0 ? 0 : nextIndex,
      answers: nextAnswers,
      validationError: null,
      validationErrorQuestionId: null,
    }
    persistProgressSnapshot({ ...current, ...nextState }, nextAnswers)
    set(nextState)
  },
  refreshAutofillAnswers: () => {
    const current = get()
    if (!current.form || current.questions.length === 0) return
    const nextAnswers = applyDefaultAnswers(current.questions, current.answers)
    if (areAnswerMapsEqual(current.answers, nextAnswers)) return
    persistProgressSnapshot(current, nextAnswers)
    set({ answers: nextAnswers })
  },
  clearAnswersByQuestionIds: (questionIds) => {
    const current = get()
    const answerIds = new Set(questionIds)
    if (answerIds.size === 0) return
    const answers = new Map(current.answers)
    let changed = false
    for (const questionId of answerIds) {
      if (answers.delete(questionId)) changed = true
    }
    if (!changed) return
    persistProgressSnapshot(current, answers)
    set({ answers, validationError: null, validationErrorQuestionId: null })
  },
  setSubmissionMetadataPatch: (patch) => {
    set({ submissionMetadataPatch: patch })
  },
  setAnswer: (questionId, value) => {
    const answers = new Map(get().answers)
    answers.set(questionId, value)
    const nextAnswers = normalizeFormAnswers(get().questions, answers)
    persistProgressSnapshot(get(), nextAnswers)
    set({ answers: nextAnswers, validationError: null, validationErrorQuestionId: null })
  },
  goNext: async () => {
    const state = get()
    const currentQuestion = state.questions[state.currentIndex]
    if (!currentQuestion) return
    const error = getQuestionValidationError(currentQuestion, state.answers.get(currentQuestion.id))
    if (error) {
      set({ validationError: error, validationErrorQuestionId: currentQuestion.id })
      return
    }
    const nextId = getNextQuestionId(
      currentQuestion.id,
      state.questions,
      state.form?.logicJumps ?? [],
      state.answers,
      state.form?.hiddenFields ?? {},
      (state.form as PublicForm & { groups?: QuestionGroupReference[] })?.groups ?? [],
    )
    if (nextId === null) {
      await get().submit()
      return
    }
    const nextIndex = state.questions.findIndex((question) => question.id === nextId)
    set({
      currentIndex: nextIndex < 0 ? state.currentIndex + 1 : nextIndex,
      direction: 'forward',
      questionStartTime: Date.now(),
      validationError: null,
      validationErrorQuestionId: null,
    })
  },
  goBack: () => {
    set((state) => ({
      currentIndex: Math.max(0, state.currentIndex - 1),
      direction: 'backward',
      validationError: null,
      validationErrorQuestionId: null,
    }))
  },
  submit: async () => {
    if (get().isSubmitting) return
    if (get().mode === 'preview') {
      set({
        isSubmitting: false,
        phase: 'thankyou',
        validationError: null,
        validationErrorQuestionId: null,
        submissionMetadataPatch: null,
      })
      return
    }

    set({ isSubmitting: true })
    await new Promise((resolve) => window.setTimeout(resolve, 500))
    try {
      if (isApiDataClientMode()) {
        await submitPublicFormResponseToApi(get())
      } else {
        createCustomerApplicationForSubmission(get())
      }
      const formId = get().form?.id
      if (formId) localStorage.removeItem(getFormProgressStorageKey(formId))
      set({
        isSubmitting: false,
        phase: 'thankyou',
        validationError: null,
        validationErrorQuestionId: null,
        submissionMetadataPatch: null,
      })
    } catch (error) {
      const submitError = resolvePublicSubmitError(error, get().questions)
      set({
        isSubmitting: false,
        validationError: submitError.message,
        validationErrorQuestionId: submitError.questionId,
        submissionMetadataPatch: null,
      })
    }
  },
}))
